/**
 * EditorShowcase — fw-choi UI 컴포넌트 쇼케이스(데모). 실제 업무 화면 아님.
 * 【역할】 공통 컴포넌트/패턴의 사용 예시를 한 화면에 모아 보여준다.
 */
import { useState } from 'react'
import { Card, Input, Typography } from 'antd'
import MDEditor from '@uiw/react-md-editor'
import PageContainer from '@/components/common/PageContainer'
import { useLabel } from '@/framework/useLabel'
import { useThemeStore } from '@/store/themeStore'

const { Title } = Typography

const cardBody = {
  body: { padding: 10, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' as const }
}
const cardOuter = { display: 'flex', flexDirection: 'column' as const, overflow: 'hidden' }

/**
 * ============================================================================
 * EditorShowcase — 텍스트/코드/마크다운 에디터 카탈로그
 * ============================================================================
 *
 * 【역할】 antd Input.TextArea 와 @uiw/react-md-editor 의 Markdown 미리보기를
 *         조합한 4-pane 에디터 비교 데모. 무거운 에디터(tiptap 등) 없이도
 *         가능한 최소 형태를 보여줌.
 *
 * 【시연 항목】
 *   - Plain Text          — 일반 textarea
 *   - Code                — monospace 폰트 textarea
 *   - Markdown (raw)      — 좌측 입력
 *   - Markdown (preview)  — MDEditor.Markdown 우측 렌더 (테마 동기화)
 *
 * 【사용처】 router/index.tsx — `{ path: '/showcase/editor' }` (lazy)
 * 【포팅 원본】 자체 작성. 본격 에디터(tiptap/md-editor)는 `/templates/editor`,
 *               `/templates/markdown` 에서 별도 데모.
 * 【의존성】 antd Input.TextArea/Card/Typography, @uiw/react-md-editor, themeStore
 * 【참고】 https://ant.design/components/input
 */
export default function EditorShowcase() {
  const { t } = useLabel()
  const themeMode = useThemeStore((s) => s.mode)
  const [markdown, setMarkdown] = useState(
    '# Hello\n\nThis is **markdown** preview.\n\n- item 1\n- item 2\n\n```ts\nconst x = 1\n```'
  )
  const [code, setCode] = useState("function hello() {\n  return 'world'\n}\n")
  const [plain, setPlain] = useState(
    t('LBL_EDITOR_PLAIN_SAMPLE', 'Simple textarea editor. 여러 줄 입력이 가능합니다.')
  )

  return (
    <PageContainer style={{ overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8, flex: '0 0 auto' }}>
        <Title level={4} style={{ margin: 0 }}>
          {t('LBL_EDITOR_COMPONENTS', 'Editor 컴포넌트')}
        </Title>
      </div>

      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gridTemplateRows: '1fr 1fr',
          gridTemplateAreas: `
            "plain code"
            "md preview"
          `,
          gap: 10
        }}
      >
        <Card
          size="small"
          title="Plain Text"
          styles={cardBody}
          style={{ ...cardOuter, gridArea: 'plain' }}
        >
          <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
            <Input.TextArea
              value={plain}
              onChange={(e) => setPlain(e.target.value)}
              style={{ height: '100%', resize: 'none' }}
            />
          </div>
        </Card>

        <Card
          size="small"
          title="Code"
          styles={cardBody}
          style={{ ...cardOuter, gridArea: 'code' }}
        >
          <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
            <Input.TextArea
              value={code}
              onChange={(e) => setCode(e.target.value)}
              style={{ height: '100%', resize: 'none', fontFamily: 'monospace', fontSize: 12 }}
            />
          </div>
        </Card>

        <Card
          size="small"
          title="Markdown (raw)"
          styles={cardBody}
          style={{ ...cardOuter, gridArea: 'md' }}
        >
          <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
            <Input.TextArea
              value={markdown}
              onChange={(e) => setMarkdown(e.target.value)}
              style={{ height: '100%', resize: 'none', fontFamily: 'monospace', fontSize: 12 }}
            />
          </div>
        </Card>

        <Card
          size="small"
          title="Markdown (preview)"
          styles={cardBody}
          style={{ ...cardOuter, gridArea: 'preview' }}
        >
          <div
            data-color-mode={themeMode}
            style={{
              flex: 1,
              minHeight: 0,
              padding: 10,
              background: 'var(--ant-color-bg-container, #ffffff)',
              color: 'var(--ant-color-text, rgba(0,0,0,0.88))',
              borderRadius: 6,
              fontSize: 13,
              overflow: 'auto',
              border: '1px solid var(--ant-color-border-secondary, #f0f0f0)'
            }}
          >
            <MDEditor.Markdown
              source={markdown}
              style={{
                background: 'transparent',
                color: 'inherit'
              }}
            />
          </div>
        </Card>
      </div>
    </PageContainer>
  )
}
